/**
 * Local log of incoming shielded notes.
 *
 * Each scanner delta returns notes addressed to our UFVK. We persist
 * those here keyed by nullifier so the Wallet's "Recent activity" can
 * show "Received +X ZEC" rows alongside outgoing tx-history.
 *
 * Storage: localStorage, capped at 200 most-recent entries per device.
 * We also de-dupe by nullifier — re-running the scanner against a block
 * we've already seen won't double-count.
 */

const KEY = "telecrypt.incoming-notes.v1";
const MAX_ENTRIES = 200;

export interface IncomingNote {
  /** Sapling nullifier — primary key, dedup target. */
  nullifier: string;
  /** Tx id of the shielded transaction this note arrived in. */
  txid: string;
  /** Value in zats. */
  value_zats: number;
  /** Block height the note was confirmed at. */
  height: number;
  /** Local time (ms) we first observed this note. */
  observed_at: number;
  /**
   * Plaintext ZIP-302 memo, decrypted by the scanner enclave using our
   * UFVK. UTF-8, up to 512 bytes. Null when the sender included no memo
   * (default).
   */
  memo: string | null;
}

function load(): IncomingNote[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as IncomingNote[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function save(list: IncomingNote[]): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX_ENTRIES)));
  } catch {
    // quota / privacy mode — silent
  }
}

/**
 * Record a batch of newly-discovered notes. De-dupes against existing
 * entries by nullifier. Returns the count actually inserted.
 */
export function recordIncomingNotes(
  notes: Array<{
    nullifier: string;
    txid: string;
    value_zats: number;
    height: number;
    memo?: string | null;
  }>,
): number {
  if (notes.length === 0) return 0;
  const list = load();
  // Existing rows index — we want to UPDATE rather than skip if the
  // new entry brings a memo we didn't have before (backfill from the
  // scanner state will fire after the original empty-memo record).
  const indexByNf = new Map(list.map((n, i) => [n.nullifier, i]));
  let mutated = 0;
  const now = Date.now();
  for (const n of notes) {
    const idx = indexByNf.get(n.nullifier);
    if (idx === undefined) {
      list.unshift({
        nullifier: n.nullifier,
        txid: n.txid,
        value_zats: n.value_zats,
        height: n.height,
        observed_at: now,
        memo: n.memo ?? null,
      });
      indexByNf.set(n.nullifier, 0);
      mutated++;
    } else {
      const existing = list[idx]!;
      // Late-arriving memo (older record persisted without it).
      if ((!existing.memo || existing.memo.length === 0) && n.memo) {
        list[idx] = { ...existing, memo: n.memo };
        mutated++;
      }
    }
  }
  if (mutated > 0) {
    list.sort((a, b) => b.observed_at - a.observed_at);
    save(list);
  }
  return mutated;
}

/** Newest-first listing for the Wallet history. */
export function recentIncomingNotes(n: number): IncomingNote[] {
  return load().slice(0, n);
}

export function listIncomingNotes(): IncomingNote[] {
  return load();
}

export function clearIncomingNotes(): void {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(KEY);
}
